import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { SectionLabel, Reveal } from '../components/Section';
import Markdown from '../components/Markdown';
import { Users, Trophy, Target, BookOpen, Handshake } from 'lucide-react';

function CollaboratorCard({ c, index }) {
  const name = c.name || c.username || 'Collaborator';
  const role = c.role === 'host_admin' ? 'Host Admin' : 'Organiser';
  return (
    <Reveal delay={(index % 4) * 0.07}>
      <div className="card-surface card-surface-hover p-6 flex items-center gap-4" data-testid={`collaborator-card-${index}`}>
        {c.avatar_url ? (
          <img src={c.avatar_url} alt={name} loading="lazy" className="w-12 h-12 rounded-full object-cover border border-[#e8173a]/30" />
        ) : (
          <span className="w-12 h-12 rounded-full bg-[#e8173a]/15 border border-[#e8173a]/30 flex items-center justify-center text-[#e8173a] font-black italic text-lg">
            {name.charAt(0).toUpperCase()}
          </span>
        )}
        <div className="min-w-0">
          <p className="text-white font-bold text-sm truncate">{name}</p>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-[#8a8a8a] mt-0.5">{role}</p>
        </div>
      </div>
    </Reveal>
  );
}

export { CollaboratorCard };

export default function About() {
  const [about, setAbout] = useState(null);
  const [stats, setStats] = useState(null);
  const [collabs, setCollabs] = useState([]);

  useEffect(() => {
    api.about().then(setAbout).catch(() => setAbout({}));
    api.stats().then(setStats).catch(() => {});
    api.collaborators().then((d) => setCollabs(d.collaborators || [])).catch(() => {});
  }, []);

  const team = about?.team_info || [];

  return (
    <main className="min-h-screen" data-testid="about-page">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-24">
        <SectionLabel testid="about-label">About KROY Esports</SectionLabel>
        <Reveal>
          <h1 className="mt-5 font-display text-white text-4xl sm:text-6xl max-w-3xl">
            Built by gamers. <span className="text-[#e8173a]">Run for gamers.</span>
          </h1>
        </Reveal>

        {stats && (
          <Reveal delay={0.1}>
            <div className="mt-12 grid grid-cols-2 gap-4 max-w-xl" data-testid="about-stats-bar">
              <div className="card-surface p-6">
                <p className="text-3xl font-black italic text-[#e8173a] flex items-center gap-2">
                  <Users className="w-6 h-6" />
                  {Number(stats.total_players || 0).toLocaleString('en-IN')}
                </p>
                <p className="mt-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#8a8a8a]">Total Players</p>
              </div>
              <div className="card-surface p-6">
                <p className="text-3xl font-black italic text-[#e8173a] flex items-center gap-2">
                  <Trophy className="w-6 h-6" />
                  {Number(stats.tournaments_completed || 0).toLocaleString('en-IN')}
                </p>
                <p className="mt-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#8a8a8a]">Tournaments Completed</p>
              </div>
            </div>
          </Reveal>
        )}

        {about === null ? (
          <div className="mt-12 h-64 rounded-xl bg-[#1a1f2e] animate-pulse" data-testid="about-skeleton" />
        ) : (
          <div className="mt-12 space-y-6 max-w-4xl">
            {about.platform_description && (
              <Reveal>
                <div className="card-surface p-7 sm:p-10" data-testid="about-platform-description">
                  <Markdown content={about.platform_description} />
                </div>
              </Reveal>
            )}
            {about.mission_statement && (
              <Reveal>
                <div className="card-surface p-7 sm:p-10 border-l-4 !border-l-[#e8173a]" data-testid="about-mission">
                  <h2 className="text-sm font-extrabold uppercase tracking-[0.25em] text-[#e8173a] flex items-center gap-2 mb-4">
                    <Target className="w-4 h-4" /> Our Mission
                  </h2>
                  <Markdown content={about.mission_statement} />
                </div>
              </Reveal>
            )}
            {about.founding_story && (
              <Reveal>
                <div className="card-surface p-7 sm:p-10" data-testid="about-founding-story">
                  <h2 className="text-sm font-extrabold uppercase tracking-[0.25em] text-[#e8173a] flex items-center gap-2 mb-4">
                    <BookOpen className="w-4 h-4" /> Our Story
                  </h2>
                  <Markdown content={about.founding_story} />
                </div>
              </Reveal>
            )}
            {team.length > 0 && (
              <Reveal>
                <div data-testid="about-team">
                  <h2 className="text-sm font-extrabold uppercase tracking-[0.25em] text-[#8a8a8a] mb-6">The Team</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {team.map((m, i) => (
                      <div key={`${m.name}-${i}`} className="card-surface p-5" data-testid={`team-member-${i}`}>
                        <p className="text-white font-bold text-sm">{m.name}</p>
                        {m.role && <p className="text-[11px] font-semibold uppercase tracking-wider text-[#e8173a] mt-1">{m.role}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              </Reveal>
            )}
          </div>
        )}

        {collabs.length > 0 && (
          <div className="mt-20" data-testid="about-collaborators">
            <h2 className="text-sm font-extrabold uppercase tracking-[0.25em] text-[#8a8a8a] flex items-center gap-2">
              <Handshake className="w-4 h-4 text-[#e8173a]" /> Collaborators
            </h2>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {collabs.map((c, i) => (
                <CollaboratorCard key={c.username || c.name || i} c={c} index={i} />
              ))}
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
